import { ReactNode, useMemo } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  TriageStateFilter,
  formatUnreadBadge,
  useMarkTriageRead,
  useMarkTriageUnread,
  useTriageList,
} from '../../api/useTriageList';
import {
  CounterStuckAutoClear,
  CounterStuckNotification,
  CounterStuckPayload,
  OrphanedAutomationFillPayload,
  OrphanedFillNotification,
  TriageBucketPage,
  TriageNotification,
  autoClearOf,
  hasNoMarketHedgeWindow,
  isAutoCleared,
} from '../../types/notifications.types';
import {
  TRIAGE_SCOPE_NOUN,
  TRIAGE_UNGROUPED_KEY,
  TRIAGE_UNGROUPED_LABEL,
  TriageScope,
  applyTriageScope,
  isFilterableTriageScope,
  readTriageScope,
  withoutTriageScope,
} from '../triage/triageDeepLink';

/**
 * The triage tray (spec §5): two buckets — *Counter stuck* and *Orphaned
 * fills* — over a server-side, global read state the operator clears
 * deliberately.
 *
 * 🚨 NOTHING is ever marked read by looking. Opening this tab, hovering a row
 * or scrolling changes no state. That is the single behavioural difference
 * from the sidebar bell (which auto-marks everything the instant its popover
 * opens) and it is the entire point of the feature.
 */

/** URL param: show the cards already cleared, alongside the open ones. */
export const TRIAGE_SHOW_READ_PARAM = 'triRead';

/** Relative "5m ago" timestamp — no date lib in the repo, so inline. */
function timeAgo(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

function qty(value: number | null | undefined): string {
  return value === null || value === undefined ? '?' : String(value);
}

/** A finite number, or `null` — an absent field is never a `0` we render. */
function num(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** Contract price the way the venues quote it — Polymarket ticks at $0.001. */
function usd(value: number): string {
  return `$${value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  })}`;
}

/**
 * What the hedge annotation is allowed to say, in the only grammar its numbers
 * support.
 *
 * 🚨 *at least*, always. `hedgedQty` is the venue ack's figure and the ack
 * UNDER-reports — Polymarket acks `delayed` / `unmatched` with `filled_qty: 0`
 * and fills afterwards — so it is a floor on what crossed, and `restedQty` a
 * ceiling on what is still open. `of Y` is only spoken when the hedge carried
 * the residual, because Y is `hedged + rested` and there is no Y without it;
 * `@ price` only when it carried a price. Nothing here is defaulted: a `0` the
 * backend never sent would read as *covered nothing* / *crossed free*.
 */
function coverageText(annotation: CounterStuckAutoClear): string {
  const rested = num(annotation.restedQty);
  const price = num(annotation.crossedPrice);
  const covered =
    rested === null
      ? `at least ${annotation.hedgedQty}`
      : `at least ${annotation.hedgedQty} of ${annotation.hedgedQty + rested}`;
  return `${covered} covered${price === null ? '' : ` @ ${usd(price)}`}`;
}

/** The bounds, spelled out — the chip and the line both carry it on hover. */
const COVERAGE_TOOLTIP =
  'Venue acks under-report fills, so the covered figure is a floor and the residual a ceiling.';

/**
 * The card is UNREAD and yet a hedge annotated it: the engine crossed part of
 * the tail and re-rested the rest. Without this line the row reads as an
 * ordinary `counter_stuck` quoting its own `unhedgedQty` — a frozen number
 * from a different instant, which the backend documents as NOT comparable with
 * the hedge's.
 */
function PartialHedgeLine({
  annotation,
}: {
  annotation: CounterStuckAutoClear;
}) {
  return (
    <Tooltip title={COVERAGE_TOOLTIP}>
      <Typography
        variant="caption"
        sx={{ color: 'warning.main', fontWeight: 700, display: 'block' }}
      >
        ⚠ partially hedged at market — {coverageText(annotation)} — residual
        open
      </Typography>
    </Tooltip>
  );
}

const MUTED_ROW_SX = { opacity: 0.55 } as const;

/* ── Rows ─────────────────────────────────────────────────────────────── */

/**
 * The escalation line, and the fix for the blind spot in §3.1: a
 * `marketHedgeWindowSecs` of `null` is the TERMINAL case — no market hedge
 * will ever fire, so this card is the last word on those contracts — and the
 * payload drawer used to render no row at all for it, making the worst case
 * the only invisible one.
 */
function MarketHedgeWindowLine({ payload }: { payload: CounterStuckPayload }) {
  if (hasNoMarketHedgeWindow(payload.marketHedgeWindowSecs)) {
    return (
      <Typography
        variant="caption"
        sx={{ color: 'error.main', fontWeight: 700, display: 'block' }}
      >
        ⛔ no market-hedge window — this card is the last word on these
        contracts
      </Typography>
    );
  }
  return (
    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
      escalates to a market hedge in {payload.marketHedgeWindowSecs}s
    </Typography>
  );
}

function RuleLink({
  ruleId,
  label,
}: {
  ruleId: string | null | undefined;
  label: string;
}) {
  if (!ruleId) return <>{label}</>;
  return (
    <Tooltip title="Open every notification for this rule">
      <Link
        component={RouterLink}
        to={`/predictions/notifications?tab=notifications&ruleId=${ruleId}`}
        underline="hover"
        color="inherit"
      >
        {label}
      </Link>
    </Tooltip>
  );
}

function ReadStateControls({
  evt,
  onRead,
  onUnread,
  busy,
  autoCleared,
}: {
  evt: Pick<TriageNotification, 'id' | 'occurredAt' | 'readAt' | 'readBy'>;
  onRead: (id: string) => void;
  onUnread: (id: string) => void;
  busy: boolean;
  /** The hedge annotation, when the card carries one. */
  autoCleared?: CounterStuckAutoClear | null;
}) {
  const read = !!evt.readAt;
  const cleared = read && isAutoCleared(evt);
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {timeAgo(evt.occurredAt)}
      </Typography>
      {cleared && (
        <Tooltip title={autoCleared ? COVERAGE_TOOLTIP : ''}>
          <Chip
            size="small"
            variant="outlined"
            color="info"
            label={
              autoCleared
                ? `auto-cleared — engine hedged at market · ${coverageText(
                    autoCleared,
                  )}`
                : 'auto-cleared — engine hedged at market'
            }
          />
        </Tooltip>
      )}
      {read ? (
        <Button size="small" disabled={busy} onClick={() => onUnread(evt.id)}>
          Unread
        </Button>
      ) : (
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          onClick={() => onRead(evt.id)}
        >
          Mark read
        </Button>
      )}
    </Stack>
  );
}

export function CounterStuckRow({
  evt,
  onRead,
  onUnread,
  busy,
}: {
  evt: CounterStuckNotification;
  onRead: (id: string) => void;
  onUnread: (id: string) => void;
  busy: boolean;
}) {
  const payload = (evt.payload ?? {}) as CounterStuckPayload;
  const parked = payload.kind === 'cycle_parked';
  const autoCleared = autoClearOf(payload);
  const name = evt.pairName ?? evt.pairId ?? 'unnamed pair';
  return (
    <Box sx={{ py: 1, ...(evt.readAt ? MUTED_ROW_SX : {}) }} data-triage-id={evt.id}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        ⚠ <RuleLink ruleId={evt.ruleId} label={name} />
      </Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>
        {parked
          ? `Cycle PARKED with an un-hedged leg — ${qty(payload.unhedgedQty)} un-hedged`
          : `${qty(payload.unhedgedQty)} un-hedged · resting ${qty(payload.restingSecs)}s`}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>
        naked leg {String(payload.makerPlatform ?? '?')}{' '}
        {String(payload.makerSide ?? '?')} · path {String(payload.path ?? '?')}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>
        counter {String(payload.platform ?? '?')}{' '}
        {String(payload.counterSide ?? '?')}
      </Typography>
      <MarketHedgeWindowLine payload={payload} />
      {!evt.readAt && autoCleared && (
        <PartialHedgeLine annotation={autoCleared} />
      )}
      <ReadStateControls
        evt={evt}
        onRead={onRead}
        onUnread={onUnread}
        busy={busy}
        autoCleared={autoCleared}
      />
    </Box>
  );
}

export function OrphanedFillRow({
  evt,
  onRead,
  onUnread,
  busy,
}: {
  evt: OrphanedFillNotification;
  onRead: (id: string) => void;
  onUnread: (id: string) => void;
  busy: boolean;
}) {
  const payload = (evt.payload ?? {}) as OrphanedAutomationFillPayload;
  const title = payload.marketTitle ?? payload.marketId ?? 'unnamed market';
  const attributed = payload.attributedBy === 'market-identity';
  const coid = payload.clientOrderId ?? null;
  const rulePrefix = payload.ruleIdPrefix ?? null;
  return (
    <Box sx={{ py: 1, ...(evt.readAt ? MUTED_ROW_SX : {}) }} data-triage-id={evt.id}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          ⚠{' '}
          {payload.marketUrl ? (
            <Link
              href={payload.marketUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="inherit"
            >
              {title}
              <OpenInNewIcon sx={{ fontSize: '0.8rem', ml: 0.3 }} />
            </Link>
          ) : (
            title
          )}
        </Typography>
        {!attributed && (
          <Chip size="small" color="warning" variant="outlined" label="✋ unattributed" />
        )}
      </Stack>
      <Typography variant="caption" sx={{ display: 'block' }}>
        {String(payload.platform ?? '?')} · {qty(payload.filledQty)} filled
      </Typography>
      <Typography variant="caption" sx={{ display: 'block' }}>
        reason: {String(payload.reason ?? 'unknown')}
      </Typography>
      {(coid || rulePrefix) && (
        <Typography variant="caption" sx={{ display: 'block' }}>
          {coid ? `coid ${coid}` : ''}
          {coid && rulePrefix ? ' · ' : ''}
          {rulePrefix ? `rule ${rulePrefix}` : ''}
        </Typography>
      )}
      {attributed ? (
        <Typography variant="caption" sx={{ display: 'block' }}>
          pair: {evt.pairName ?? evt.pairId ?? 'unnamed pair'}
        </Typography>
      ) : (
        <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
          this fill could NOT be tied to a pair — no row anywhere carries its mark
        </Typography>
      )}
      <ReadStateControls evt={evt} onRead={onRead} onUnread={onUnread} busy={busy} />
    </Box>
  );
}

/* ── Trays ────────────────────────────────────────────────────────────── */

function TriageTray<TPayload>({
  title,
  page,
  renderRow,
  onMarkAllShown,
  busy,
  scoped,
}: {
  title: string;
  page: TriageBucketPage<TPayload>;
  renderRow: (evt: TriageNotification<TPayload>) => ReactNode;
  onMarkAllShown: (ids: string[]) => void;
  busy: boolean;
  /** A scope is hiding cards: the chip must not read as the whole bucket. */
  scoped: boolean;
}) {
  const { items } = page;
  // EXACTLY the ids on screen — never a query. Nothing the operator has not
  // seen can be silenced by this button, and a deep-link scope can only
  // narrow what is on screen, never widen it.
  const shownUnreadIds = useMemo(
    () => items.filter((evt) => !evt.readAt).map((evt) => evt.id),
    [items],
  );
  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Chip
          size="small"
          color={page.unread > 0 ? 'warning' : 'default'}
          data-triage-tray-unread={page.unread}
          label={
            scoped
              ? `${shownUnreadIds.length} of ${formatUnreadBadge(
                  page.unread,
                  !!page.capped,
                )} unread shown`
              : `${formatUnreadBadge(page.unread, !!page.capped)} unread`
          }
        />
      </Stack>
      <Divider sx={{ my: 1 }} />
      {items.length === 0 ? (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {scoped ? 'No card here matches the filter.' : 'Nothing here.'}
        </Typography>
      ) : (
        <Box sx={{ maxHeight: '60vh', overflowY: 'auto', overflowAnchor: 'auto' }}>
          {items.map((evt) => (
            <Box key={evt.id}>
              {renderRow(evt)}
              <Divider />
            </Box>
          ))}
        </Box>
      )}
      <Box sx={{ pt: 1 }}>
        <Button
          size="small"
          disabled={busy || shownUnreadIds.length === 0}
          onClick={() => onMarkAllShown(shownUnreadIds)}
        >
          Mark all shown read
        </Button>
      </Box>
    </Paper>
  );
}

/* ── Deep-link scope ──────────────────────────────────────────────────── */

/**
 * A mark on a row is a pointer at SOME cards, so the tray it opens has to say
 * which ones. It FILTERS rather than highlights: the trays share one
 * `Mark all shown read` button whose scope is "the ids on screen", and a
 * highlight would leave that button covering 180 cards while the operator
 * believes they are looking at one row's.
 *
 * 🚨 A filtered tray must never present as the whole tray — that is the same
 * "clean screen over an open leg" the feature exists to prevent — so the
 * banner is unconditional, says what it is hiding, and clears in one click.
 *
 * What it can count is THIS PAGE: `hidden` is the cards the fetched page holds
 * and the scope rejects, and the page is capped (`TRIAGE_LIST_LIMIT`). With 300
 * unread and 100 fetched, "98 hidden" would be 198 short — and with *show read*
 * on it also counts cleared cards. So the line says *of this page*, and the
 * bucket chip beside it keeps reporting the server's own unread total.
 */
function scopedBucket<TPayload>(
  page: TriageBucketPage<TPayload>,
  scope: TriageScope | null,
): { page: TriageBucketPage<TPayload>; hidden: number } {
  const items = applyTriageScope(page.items, scope);
  return { page: { ...page, items }, hidden: page.items.length - items.length };
}

function scopeLabel(scope: TriageScope): string {
  if (scope.kind === 'groupId' && scope.id === TRIAGE_UNGROUPED_KEY) {
    return TRIAGE_UNGROUPED_LABEL;
  }
  return `${TRIAGE_SCOPE_NOUN[scope.kind]} ${scope.id}`;
}

function TriageScopeBanner({
  scope,
  shown,
  hidden,
  onClear,
}: {
  scope: TriageScope;
  shown: number;
  hidden: number;
  onClear: () => void;
}) {
  const clear = (
    <Button size="small" onClick={onClear} data-triage-scope-clear="1">
      {isFilterableTriageScope(scope) ? 'Show all cards' : 'Clear'}
    </Button>
  );

  if (!isFilterableTriageScope(scope)) {
    return (
      <Alert
        severity="warning"
        action={clear}
        data-triage-scope="unfilterable"
        sx={{ mt: 1 }}
      >
        Opened from the badge for {scopeLabel(scope)} — a triage card carries no
        group, so the tray below is NOT filtered to it. Everything open is
        shown.
      </Alert>
    );
  }

  return (
    <Alert
      severity="info"
      action={clear}
      data-triage-scope="filtered"
      sx={{ mt: 1 }}
    >
      Filtered to {scopeLabel(scope)} — showing {shown} card
      {shown === 1 ? '' : 's'}, {hidden} more on this page hidden by the
      filter. The tray is paged, so this counts only what was fetched.
    </Alert>
  );
}

/* ── The tab ──────────────────────────────────────────────────────────── */

export function TriageTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const showRead = searchParams.get(TRIAGE_SHOW_READ_PARAM) === '1';
  const state: TriageStateFilter = showRead ? 'all' : 'unread';

  const setShowRead = (next: boolean) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next) p.set(TRIAGE_SHOW_READ_PARAM, '1');
        else p.delete(TRIAGE_SHOW_READ_PARAM);
        return p;
      },
      { replace: true },
    );
  };

  const scope = readTriageScope(searchParams);
  // Only a pair/rule scope can hide a card; a group scope hides nothing.
  const filtering = isFilterableTriageScope(scope);
  const clearScope = () => {
    setSearchParams((prev) => withoutTriageScope(new URLSearchParams(prev)), {
      replace: true,
    });
  };

  const { data, isStale, lastSuccessAt } = useTriageList({ state });
  const markRead = useMarkTriageRead();
  const markUnread = useMarkTriageUnread();
  const busy = markRead.isLoading || markUnread.isLoading;

  const counter = scopedBucket<CounterStuckPayload>(
    data?.counter ?? { unread: 0, items: [] },
    scope,
  );
  const orphan = scopedBucket<OrphanedAutomationFillPayload>(
    data?.orphan ?? { unread: 0, items: [] },
    scope,
  );

  const onRead = (id: string) => markRead.mutate([id]);
  const onUnread = (id: string) => markUnread.mutate([id]);
  const onMarkAllShown = (ids: string[]) => {
    if (ids.length === 0) return;
    // The count is in the confirm because the button's scope is what is on
    // screen right now, and that is not obvious from its label.
    if (!window.confirm(`Mark ${ids.length} shown cards read?`)) return;
    markRead.mutate(ids);
  };

  return (
    <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        <Typography variant="body2">
          {data
            ? data.oldestUnreadAt
              ? `Oldest unread: ${timeAgo(data.oldestUnreadAt)}`
              : 'Nothing unread.'
            : ''}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showRead}
              onChange={(e) => setShowRead(e.target.checked)}
            />
          }
          label={
            <Typography variant="caption">show read (last 24h)</Typography>
          }
        />
      </Stack>

      {/* 🚨 Keyed on the DATA, never on `isError`: react-query's error flag is
          sticky and retains `data`, so a tray that read it as truth would go
          blank over data it still had — and a tray with no rows asserts "no
          naked legs". */}
      {data === undefined ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Triage unreadable — the tray could not be loaded. This is NOT
          &quot;nothing open&quot;.
        </Alert>
      ) : (
        <>
          {scope && (
            <TriageScopeBanner
              scope={scope}
              shown={counter.page.items.length + orphan.page.items.length}
              hidden={counter.hidden + orphan.hidden}
              onClear={clearScope}
            />
          )}
          {isStale && (
            <Alert severity="info" sx={{ mt: 1 }}>
              Stale — last read{' '}
              {lastSuccessAt ? timeAgo(new Date(lastSuccessAt).toISOString()) : 'a while ago'}.
            </Alert>
          )}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            sx={{ mt: 1.5, alignItems: 'flex-start' }}
          >
            <TriageTray
              title="Counter stuck"
              page={counter.page}
              scoped={filtering}
              busy={busy}
              onMarkAllShown={onMarkAllShown}
              renderRow={(evt) => (
                <CounterStuckRow
                  evt={evt}
                  onRead={onRead}
                  onUnread={onUnread}
                  busy={busy}
                />
              )}
            />
            <TriageTray
              title="Orphaned fills"
              page={orphan.page}
              scoped={filtering}
              busy={busy}
              onMarkAllShown={onMarkAllShown}
              renderRow={(evt) => (
                <OrphanedFillRow
                  evt={evt}
                  onRead={onRead}
                  onUnread={onUnread}
                  busy={busy}
                />
              )}
            />
          </Stack>
        </>
      )}
    </Box>
  );
}
